import { useEffect, useState, useCallback, useRef } from 'react';
import { ThemeCtx, THEME, T, Ico, Toast, AppHeader } from '../design';
import type { ToastData } from '../design';
import { PortalProvider, usePortal } from '../context/PortalContext';
import { useNavStack } from './navigation';
import { Launcher } from './Launcher';
import { CoursesModule } from '../modules/courses/CoursesModule';
import { ClaimsModule } from '../modules/claims/ClaimsModule';
import { PrimeModule } from '../modules/prime/PrimeModule';
import { PrimeStatsModule } from '../modules/prime-stats/PrimeStatsModule';
import { ContextBanner } from '../modules/courses/screens/ContextBanner';
import { AdminPicker } from './AdminPicker';

/** Midlertidig pladsholder — erstattes når modulet bygges i sin fase. */
function ModulePlaceholder({ name }: { name: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 16px', color: T.fg3, fontWeight: 600 }}>
      {name} bygges i en senere fase.
    </div>
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, maxWidth: 440, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', fontFamily: T.font, color: T.fg2, fontWeight: 600 }}>
      {children}
    </div>
  );
}

function Shell() {
  const portal = usePortal();
  const nav = useNavStack({ type: 'home' });

  const [toast, setToast] = useState<ToastData | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fireToast = useCallback((next: ToastData) => {
    setToast(next);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  useEffect(() => {
    portal.initializePortal();
    // initializePortal er stabil (useCallback); kør kun ved mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (portal.isLoading) return <FullScreen>Indlæser…</FullScreen>;
  if (portal.error) return <FullScreen>{portal.error}</FullScreen>;
  if (!portal.isAuthenticated) return <FullScreen>Mangler portal-adgang.</FullScreen>;

  const employeeName = portal.employee?.name ?? portal.actor?.name ?? 'PARK Admin';
  const salonName = portal.salon?.name ?? (portal.isAdminMode ? 'Admin-mode' : '');

  const cur = nav.current;
  const COURSE_SCREENS = new Set(['kurser', 'course-detail', 'courses-bulk']);
  const CLAIM_SCREENS = new Set(['claims', 'claims-list', 'claims-create', 'claims-item', 'claims-detail']);
  const PRIME_SCREENS = new Set(['medlem', 'members-search', 'members-detail']);
  const PRIME_STATS_SCREENS = new Set(['prime', 'prime-salon', 'prime-employee']);

  // En intern bruger bag sessionen, som agerer som en medarbejder.
  const impersonating = portal.actor != null && portal.employee != null;
  const frame = (content: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: T.bg, maxWidth: 440, margin: '0 auto', boxShadow: '0 0 60px rgba(0,0,0,0.10)', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {impersonating && <ContextBanner salonName={portal.salon?.name ?? ''} employeeName={portal.employee?.name ?? ''} onChange={() => portal.stopImpersonation()} />}
      {content}
      <Toast toast={toast} />
    </div>
  );

  // Kurser, reklamationer og PRIME-medlemmer kører som en medarbejder: i admin-tilstand vælges en først.
  if (portal.isAdminMode && (COURSE_SCREENS.has(cur.type) || CLAIM_SCREENS.has(cur.type) || PRIME_SCREENS.has(cur.type))) {
    return frame(<AdminPicker onBack={nav.back} />);
  }

  if (COURSE_SCREENS.has(cur.type)) return frame(<CoursesModule screen={cur} nav={nav} fireToast={fireToast} />);
  if (CLAIM_SCREENS.has(cur.type)) return frame(<ClaimsModule screen={cur} nav={nav} fireToast={fireToast} />);
  if (PRIME_SCREENS.has(cur.type)) return frame(<PrimeModule screen={cur} nav={nav} fireToast={fireToast} />);
  if (PRIME_STATS_SCREENS.has(cur.type)) return frame(<PrimeStatsModule screen={cur} nav={nav} />);

  let header: React.ReactNode;
  let body: React.ReactNode;

  if (cur.type === 'home') {
    header = (
      <AppHeader
        eyebrow="PARK Partner"
        title="Portal"
        right={
          <button aria-label="Notifikationer" style={{ width: 38, height: 38, borderRadius: 11, border: 0, background: T.surface, color: T.headerFg, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Ico.bell size={18} />
          </button>
        }
      />
    );
    body = (
      <Launcher
        employeeName={employeeName}
        salonName={salonName}
        onOpen={(key) => nav.push({ type: key })}
      />
    );
  } else {
    header = <AppHeader eyebrow="Modul" title="Modul" onBack={nav.back} />;
    body = <ModulePlaceholder name="Modulet" />;
  }

  return frame(
    <>
      {header}
      <main style={{ flex: 1, padding: '18px 14px calc(36px + env(safe-area-inset-bottom))' }}>{body}</main>
    </>
  );
}

export function PortalApp() {
  return (
    <ThemeCtx.Provider value={THEME}>
      <PortalProvider>
        <Shell />
      </PortalProvider>
    </ThemeCtx.Provider>
  );
}
